import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useDeptTabParam } from './useDeptTabParam';
import DepartmentStatStrip from './DepartmentStatStrip';
import ToolsTab from './tools/ToolsTab';
import MembersTab from './members/MembersTab';
import DeniedState from '../common/DeniedState';
import './department.css';

// Kept on every state (loading, denied, error, ready) so the title never jumps.
const EYEBROW = 'Department manager view';

const TABS = [
  { id: 'tools', label: 'Tools' },
  { id: 'members', label: 'Members' },
];

/**
 * Per-department manager page. Tabbed: Tools | Members.
 * Header carries name + a Tools/Members/Pending stat strip.
 *
 * Status is one of loading | ready | forbidden | error:
 *  - forbidden ONLY on a 403, a missing department, or a non-numeric id. It
 *    must not leak the department name.
 *  - any other failure is an error with Try again; telling a real manager
 *    "you don't manage this department" during an outage would be a lie.
 * The managed list is read here rather than through MyDepartmentsNav's
 * useManagedDepartments: that hook folds every failure into [] (right for a
 * nav that just hides), which would erase the 403-vs-outage distinction.
 * The tabs mount only once the department resolves, so a non-manager never
 * fires the catalog/roster calls (they would 403 behind the denied state).
 */
export default function DepartmentPage() {
  const { id } = useParams();
  const deptId = /^\d+$/.test(id || '') ? parseInt(id, 10) : NaN;
  const { tab, setTab } = useDeptTabParam();

  const [status, setStatus] = useState('loading');
  const [dept, setDept] = useState(null);
  const [toolCount, setToolCount] = useState(null);
  const [memberCount, setMemberCount] = useState(null);
  const activeRef = useRef(true);
  const tabRefs = useRef({});

  // Department name + counts come from the managed list (cheap, already gated).
  // `quiet` re-reads without blanking the page (after a team request).
  const loadDept = useCallback(async ({ quiet = false } = {}) => {
    if (Number.isNaN(deptId)) { setStatus('forbidden'); return; }
    if (!quiet) setStatus('loading');
    try {
      const r = await axios.get(`${API_BASE_URL}/api/dept/managed`, { withCredentials: true });
      if (!activeRef.current) return;
      const found = (r.data.departments || []).find((d) => d.id === deptId);
      if (!found) { setStatus('forbidden'); return; }
      setDept(found);
      setMemberCount(found.member_count);
      setStatus('ready');
    } catch (err) {
      if (!activeRef.current || quiet) return;
      setStatus(err?.response?.status === 403 ? 'forbidden' : 'error');
    }
  }, [deptId]);

  // A department switch (/dept/62 → /dept/63 reuses this component) must not
  // keep showing the previous department's name and counts.
  useEffect(() => {
    activeRef.current = true;
    setDept(null);
    setToolCount(null);
    setMemberCount(null);
    loadDept();
    return () => { activeRef.current = false; };
  }, [loadDept]);

  // The Tools count is otherwise only filled by ToolsTab; landing on Members
  // would leave it blank.
  useEffect(() => {
    // dept.id check: on a department switch the first render still holds the
    // previous department, and this must not fetch the new id before
    // ownership is confirmed.
    if (status !== 'ready' || !dept || dept.id !== deptId || tab === 'tools' || toolCount !== null) return undefined;
    let active = true;
    axios
      .get(`${API_BASE_URL}/api/dept/${deptId}/catalog`, { withCredentials: true })
      .then((r) => { if (active) setToolCount((r.data.tools || []).length); })
      .catch(() => { /* fail open: the stat stays "—" (unknown), never 0 */ });
    return () => { active = false; };
  }, [status, dept, tab, deptId, toolCount]);

  const onTabKeyDown = (e) => {
    const idx = TABS.findIndex((t) => t.id === tab);
    let next = null;
    if (e.key === 'ArrowRight') next = TABS[(idx + 1) % TABS.length];
    else if (e.key === 'ArrowLeft') next = TABS[(idx - 1 + TABS.length) % TABS.length];
    else if (e.key === 'Home') next = TABS[0];
    else if (e.key === 'End') next = TABS[TABS.length - 1];
    if (!next) return;
    e.preventDefault();
    setTab(next.id);
    tabRefs.current[next.id]?.focus();
  };

  if (status === 'forbidden') {
    return (
      <DeniedState
        className="dept2-page"
        eyebrow={EYEBROW}
        title="Department unavailable"
        message="You don't manage this department."
      />
    );
  }

  if (status === 'error') {
    return (
      <div className="dept2-page">
        <header className="page-head dept2-state">
          <p className="page-eyebrow">{EYEBROW}</p>
          <h1 className="page-title">Department</h1>
          <div className="dept2-error" role="alert">
            <p>Couldn&apos;t load this department. Check your connection and try again.</p>
            <button type="button" className="btn btn-secondary btn-small" onClick={() => loadDept()}>Try again</button>
          </div>
        </header>
      </div>
    );
  }

  // `dept.id !== deptId`: one frame after a switch still holds the old
  // department; render the loading state rather than its name and tabs.
  if (status === 'loading' || !dept || dept.id !== deptId) {
    return (
      <div className="dept2-page" role="status" aria-busy="true" aria-label="Loading department">
        <header className="page-head">
          <p className="page-eyebrow">{EYEBROW}</p>
          <span className="dept2-title-skeleton" aria-hidden="true" />
        </header>
      </div>
    );
  }

  const counts = { tools: toolCount, members: memberCount };

  return (
    <div className="dept2-page">
      <header className="page-head">
        <p className="page-eyebrow">{EYEBROW}</p>
        <h1 className="page-title">{dept.name}</h1>
      </header>

      <DepartmentStatStrip
        tools={toolCount}
        members={memberCount}
        pending={dept.pending_request_count}
      />

      <div className="dept2-tabs" role="tablist" aria-label="Department sections">
        {TABS.map((t) => {
          const selected = tab === t.id;
          return (
            <button
              key={t.id}
              ref={(el) => { tabRefs.current[t.id] = el; }}
              id={`dept2-tab-${t.id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              // Only the active panel exists, so only it is referenced.
              aria-controls={selected ? `dept2-panel-${t.id}` : undefined}
              tabIndex={selected ? 0 : -1}
              className={selected ? 'dept2-tab active' : 'dept2-tab'}
              onClick={() => setTab(t.id)}
              onKeyDown={onTabKeyDown}
            >
              {t.label}{counts[t.id] !== null && counts[t.id] !== undefined ? ` (${counts[t.id]})` : ''}
            </button>
          );
        })}
      </div>

      <div
        id={`dept2-panel-${tab}`}
        role="tabpanel"
        aria-labelledby={`dept2-tab-${tab}`}
      >
        {tab === 'tools'
          ? <ToolsTab deptId={deptId} onCount={setToolCount} onRequestSubmitted={() => loadDept({ quiet: true })} />
          : <MembersTab deptId={deptId} onCount={setMemberCount} />}
      </div>
    </div>
  );
}
